import cloneDeep from 'lodash/cloneDeep';

import { DesignData } from 'editor/src/store/design/types';

import { ReflectContext } from './getReflectContext';
import reflectSpreadAndApply from './reflectSpreadAndApply';
import { Mode } from './utils';

// GAPI-30848: tech suffixes attached to multi-tech apparel print-area spread
// names. Keep in sync with `multi-tech-iframe-bridge.service.ts` on the
// storefront side. Used to extract the canonical area (e.g. "back" from
// "back-dtg") so a saved DTG spread can be reflected onto a catalogue-default
// DTF spread for the same area when exact-name lookup misses.
const MULTI_TECH_SUFFIXES = ['-dtf', '-dtg', '-embroidery', '-emb'];

export const getCanonicalArea = (name: string | undefined): string => {
  if (!name) {
    return '';
  }
  const lc = name.toLowerCase();
  const suffix = MULTI_TECH_SUFFIXES.find((s) => lc.endsWith(s));
  return suffix ? lc.slice(0, -suffix.length) : lc;
};

function reflectDesignData(
  sourceDesign: DesignData,
  destDesign: DesignData,
  context: ReflectContext,
  mode: Mode = 'adapt',
): DesignData {
  const { groupedSpreadsPerProductUids } = context;
  const spreadGroups = groupedSpreadsPerProductUids?.[destDesign.product_uid];
  const newDesign = cloneDeep(destDesign);
  newDesign.spread_groups = spreadGroups;
  newDesign.lifecycle_metadata = cloneDeep(sourceDesign.lifecycle_metadata);
  newDesign.color_space = sourceDesign.color_space;

  newDesign.plugins = cloneDeep(sourceDesign.plugins);
  newDesign.personalizationSettings = cloneDeep(sourceDesign.personalizationSettings);
  newDesign.calendar = cloneDeep(sourceDesign.calendar);
  // Reflected elements keep their code-set columns, so the binding they name has
  // to travel with them or the variant exports bound texts with nothing to fill them.
  newDesign.variableData = cloneDeep(sourceDesign.variableData);
  // Reflected texts keep their style references, so the styles travel with them. The
  // destination's own styles stay too: texts on spreads nothing reflects onto still
  // name them. Same id on both sides: the source's version wins.
  const sourceStyleIds = new Set(sourceDesign.textStyles?.map(({ id }) => id));
  const textStyles = [
    ...cloneDeep(sourceDesign.textStyles ?? []),
    ...(destDesign.textStyles ?? []).filter(({ id }) => !sourceStyleIds.has(id)).map((style) => ({ ...style })),
  ];
  newDesign.textStyles = textStyles.length ? textStyles : undefined;

  // GAPI-39157: track which dest spreads have already been reflected onto so
  // that a single dest spread is not overwritten by multiple source spreads.
  const usedDestIndices = new Set<number>();

  sourceDesign.spreads.forEach((sourceSpread, spreadIndex) => {
    // GAPI-30848 — exact-name match first (preserves the legacy behaviour for
    // single-tech variants and for cases where the bridge has already injected
    // a matching placeholder spread). Fall back to canonical-area matching so
    // a saved mixed-tech spread (e.g. `back-dtg`) can reflect onto the
    // catalogue's default per-area spread (`back-dtf`) when the wizard's
    // `designTemplates` payload ships all-DTF names. Without this fallback,
    // 3-area-DTG + 1-area-DTF saves reopen with empty canvases because
    // reflectSpreadAndApply never runs for the saved spreads.
    const destSpread =
      newDesign.spreads.find((spread, i) => !usedDestIndices.has(i) && spread.name === sourceSpread.name) ??
      newDesign.spreads.find(
        (spread, i) =>
          !usedDestIndices.has(i) &&
          getCanonicalArea(spread.name) === getCanonicalArea(sourceSpread.name) &&
          getCanonicalArea(spread.name) !== '',
      ) ??
      // GAPI-39157: fall back to positional matching when name-based lookup
      // fails. This covers cross-spread-count reflection for flyers where
      // the source design uses names like "Front"/"Back" but the dest
      // template uses numeric names (or vice-versa). Without this fallback
      // no source spread matches and the reflected design is blank.
      (spreadIndex < newDesign.spreads.length && !usedDestIndices.has(spreadIndex)
        ? newDesign.spreads[spreadIndex]
        : undefined);
    if (!destSpread) {
      return;
    }

    usedDestIndices.add(newDesign.spreads.indexOf(destSpread));

    reflectSpreadAndApply({
      sourceSpread,
      destSpread,
      sourceDesign,
      newDesign,
      sourceSpreadIndex: spreadIndex,
      context,
      mode,
    });
  });
  return newDesign;
}

export default reflectDesignData;
